import "@/shared/lib/polyfills"; // 첫 import — 다른 모듈(react-virtuoso 등) 평가 전에 런타임 API 보강
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import "@/styles/global.scss";
import { registerChunkReloadRecovery } from "@/shared/lib/chunkReloadRecovery";
import { initSentry, sentryRootOptions } from "@/shared/config/sentry";
import { resetBackContract } from "@/shared/lib/backBridge";
import { queryClient } from "@/shared/api";
import { canonicalizeGateExceptionUrl, isStoreGated } from "@/shared/lib/storeGate";
import StoreLanding from "@/pages/store-landing/StoreLanding";
import App from "./App";

// 관측/복구 계열은 React 트리 밖 최상단 1회 등록. 렌더 실패와 무관하게 동작한다.
// Sentry 초기화(#263) → 청크 로드 실패 자체 복구(#198) 순으로 최상단 배치.
initSentry();
registerChunkReloadRecovery();
// 콜드스타트/리로드 시 네이티브 back 계약 선제 리셋 — 이 버그의 확정 복구 축.
// 이전 세션(카드결제 window.location.assign 언로드 등)이 남긴 stuck overlay/stepNav 플래그를 청소한다.
// React 마운트 전이라 정당한 등록자가 0 → 강제 클리어가 안전(콜드스타트 전제, bfcache 아님).
resetBackContract();

// TSQ devtools — vite serve(DEV)에서만 동적 import. 빌드에선 import.meta.env.DEV 가 false 리터럴로 치환돼
// 이 분기와 dynamic import 가 통째로 제거된다(패키지의 NODE_ENV 자체 게이트와 별개로 번들 미포함 보장).
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

const root = createRoot(document.getElementById("root")!, sentryRootOptions());

// prd 브라우저 게이트 1차(#401) — UA 토큰(maketrend/Android|iOS) 없는 앱 밖 브라우저는 App(라우터·
// QueryClient·브릿지 등록)을 마운트하지 않고 스토어 랜딩만 렌더한다. App 은 정적 import 유지 —
// 앱 WebView 경로의 부팅 순서·네이티브 콜백(onEasyPayReturn·스플래시) 등록 시점이 바뀌지 않는다.
// `=== "prd"` 리터럴을 조건에 직접 둔다 — 비-prd 빌드에서 이 블록과 StoreLanding 참조가 통째로 접힌다.
// 예외 경로 URL 정규화(/terms/index.html → /terms)는 라우터 매칭 정합용, 판정보다 먼저.
if (import.meta.env.MODE === "prd") canonicalizeGateExceptionUrl();
if (import.meta.env.MODE === "prd" && isStoreGated()) {
  root.render(
    <StrictMode>
      <StoreLanding />
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </QueryClientProvider>
    </StrictMode>,
  );
}
